/**
 * Settings → Backup & Restore.
 *
 * WHY THIS SCREEN IS A RELEASE BLOCKER
 * -----------------------------------
 * The backup service has existed for a while, and a service nobody can reach is
 * not a backup. For an application whose entire purpose is being the record of
 * invoices a business issued, "every invoice lives in one browser profile and the
 * way to keep it is not to clear your browsing data" is not a product. So the
 * SCREEN is the deliverable; the service underneath it is already proven.
 *
 * THE ORDER ON SCREEN IS THE DESIGN
 * ---------------------------------
 * Everything destructive is behind a review, and the review shows what will be
 * REPLACED before anything is replaced:
 *
 *   1. Backup Now  — safe, so it is the primary action and the first thing shown.
 *   2. Restore     — the file is validated and summarised BEFORE the button
 *                    becomes live, so "Restore" is never a leap of faith.
 *   3. Confirm     — a checkbox, because a guard that only asks once is a guard
 *                    people learn to click past.
 *
 * The counts on the review panel are the point. "This backup contains 2
 * companies, 41 contacts, 18 items and 3 documents" tells the user whether they
 * picked the right file — which is the question they actually have — and no
 * generic "are you sure?" dialog has ever provided it.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Download, ShieldCheck, Upload } from 'lucide-react';
import { PageHeader } from '@ui/components/PageHeader';
import { Button } from '@ui/components/Button';
import { ErrorState } from '@ui/components/States';
import { toast } from '@ui/components/Toaster';
import { getDb } from '@data/db/database';
import {
  BackupError,
  createBackup,
  currentDataVersion,
  restoreBackup,
} from '@data/backup/backupService';
import { parseBackup, type BackupFile } from '@data/backup/backupFormat';
import {
  getDiskBackupStatus,
  enableDiskBackup,
  reconnectDiskBackup,
  runDiskBackup,
  type DiskBackupStatus,
} from '@data/backup/localBackupEngine';
import type { BackupReadiness } from '@data/backup/diskBackupTarget';
import { resolveDiskBackupTarget } from '@data/backup/resolveDiskBackupTarget';
import { useAuth } from '@platform/auth/authContext';
import {
  AUTO_BACKUP_SCHEDULER_TOKEN,
  ensureAutoBackupScheduler,
  getBackupScheduler,
} from '@data/backup/backupScheduler';
import { deriveDiskBackupView, type DiskBackupActionKind } from './diskBackupView';
import { planRestoreCompletion } from './restoreCompletion';
import { INVENTORY_QUERY_ROOTS } from '@features/documents/useDocuments';

/** What the review panel can tell the user about a candidate backup file. */
interface Review {
  readonly file: BackupFile;
  /** The businesses this file will restore, by name. */
  readonly businessNames: readonly string[];
  readonly businessCount: number;
  readonly documentCount: number;
  readonly partyCount: number;
  readonly productCount: number;
  readonly dataVersion: number;
  readonly takenAt: string;
}

type Phase =
  | { kind: 'idle' }
  | { kind: 'busy'; what: 'backup' | 'restore' }
  | { kind: 'review'; review: Review }
  | { kind: 'invalid'; message: string }
  | {
      kind: 'done';
      message: string;
      /** Set only by a restore: where to go once the message has been read. */
      continuation?: { readonly navigateTo: string; readonly afterMs: number };
    };

/** Trigger a browser download without a server. The whole app is offline-first. */
function downloadJson(fileName: string, json: string): void {
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoking immediately can cancel the download in some browsers; one turn of
  // the event loop is enough for the click to have been handled.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function BackupRestoreScreen() {
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [acknowledged, setAcknowledged] = useState(false);
  const [dataVersion, setDataVersion] = useState<number | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    void currentDataVersion(getDb()).then(setDataVersion);
  }, []);

  /**
   * A finished restore says so before it moves on.
   *
   * Navigating in the same tick as `setPhase` re-routes and unmounts this
   * screen before React can paint the result, so the user never read it. The
   * timer is cleared on unmount as well: if the user navigates themselves
   * first, the pending redirect must not drag them back.
   */
  useEffect(() => {
    if (phase.kind !== 'done' || !phase.continuation) return;
    const { navigateTo, afterMs } = phase.continuation;
    const timer = setTimeout(() => {
      window.location.hash = navigateTo;
    }, afterMs);
    return () => clearTimeout(timer);
  }, [phase]);

  /**
   * Every cache root, invalidated after a restore.
   *
   * A restore replaces the data underneath the app. A list still holding another
   * company's rows would be a cross-company data LEAK, not a stale display — so
   * this is a correctness fix, not a nicety.
   */
  const refreshEverything = () => {
    for (const root of INVENTORY_QUERY_ROOTS) queryClient.invalidateQueries({ queryKey: [root] });
  };

  async function onBackupNow() {
    setPhase({ kind: 'busy', what: 'backup' });
    try {
      const summary = await createBackup(getDb(), { now: new Date().toISOString() });
      downloadJson(summary.fileName, summary.json);
      setPhase({
        kind: 'done',
        message: `Backup saved: ${summary.rowCount} records from ${summary.businessIds.length} business(es).`,
      });
      toast({ tone: 'success', title: 'Backup file downloaded.' });
    } catch (error) {
      setPhase({ kind: 'invalid', message: errorMessage(error) });
    }
  }

  async function onPickFile(file: File | undefined) {
    if (!file) return;
    setAcknowledged(false);
    setPhase({ kind: 'busy', what: 'restore' });
    try {
      const text = await file.text();
      const parsed = parseBackup(text);
      if (!parsed.ok || !parsed.file) {
        setPhase({
          kind: 'invalid',
          message: parsed.message ?? 'That file is not a usable backup.',
        });
        return;
      }
      const f = parsed.file;
      // Counts are read from the FILE, never from the live database. The whole
      // point of the review panel is to answer "is this the right file?", and
      // that question is only answerable from the file itself.
      setPhase({
        kind: 'review',
        review: {
          file: f,
          // NAMES, not just a count. "2 businesses" does not tell the user
          // whether they picked the right file; "Alpha Trading, Beta Supplies"
          // does. Counts alone would leave a user restoring a colleague's
          // backup without ever noticing.
          businessNames: (f.tables.businesses ?? []).map((b) =>
            String(
              (b as { legalName?: string }).legalName ?? (b as { id?: string }).id ?? 'Unnamed',
            ),
          ),
          businessCount: f.businessIds.length,
          documentCount: f.tables.documents?.length ?? 0,
          partyCount: f.tables.parties?.length ?? 0,
          productCount: f.tables.products?.length ?? 0,
          dataVersion: f.dataVersion,
          takenAt: f.takenAt,
        },
      });
    } catch (error) {
      setPhase({ kind: 'invalid', message: errorMessage(error) });
    }
  }

  async function onConfirmRestore() {
    if (phase.kind !== 'review') return;
    setPhase({ kind: 'busy', what: 'restore' });
    try {
      const summary = await restoreBackup(getDb(), phase.review.file, {
        // Hard-coded true, reachable only from a button disabled until the box
        // is ticked. The service refuses without it, so the guard lives in two
        // places and neither alone is load-bearing.
        confirmed: true,
        now: new Date().toISOString(),
      });
      refreshEverything();
      const completion = planRestoreCompletion(summary.businesses);
      setPhase({
        kind: 'done',
        message: completion.message,
        continuation: { navigateTo: completion.navigateTo, afterMs: completion.afterMs },
      });
      toast({ tone: 'success', title: 'Restore complete.' });
    } catch (error) {
      // The service is transactional, so a failure here means NOTHING changed.
      // Saying so matters: otherwise a user retries "to be safe" against a
      // database that was already intact.
      setPhase({ kind: 'invalid', message: `${errorMessage(error)} Nothing was changed.` });
      refreshEverything();
    }
  }

  return (
    <section data-testid="backup-restore-screen">
      <PageHeader
        title="Backup & Restore"
        description="Save a full offline copy of your data, or restore one. Nothing leaves this device."
        action={
          <Button
            variant="primary"
            onClick={onBackupNow}
            loading={phase.kind === 'busy' && phase.what === 'backup'}
            loadingText="Backing up…"
            icon={<Download className="h-4 w-4" aria-hidden />}
          >
            Backup Now
          </Button>
        }
      />

      <div className="space-y-4">
        <section className="rounded-lg border border-slate-800 p-4">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-100">
            <ShieldCheck className="h-4 w-4 text-emerald-500" aria-hidden />
            What a backup contains
          </h2>
          <p className="mt-1 text-xs text-slate-400">
            Every business, contact, item, document, payment and stock movement stored on this
            device, in a versioned JSON file you can keep anywhere. Backup format version 1
            {dataVersion === null ? '' : ` · this app's data version ${dataVersion}`}.
          </p>
        </section>

        <section className="rounded-lg border border-slate-800 p-4">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-100">
            <Upload className="h-4 w-4" aria-hidden />
            Restore from a backup
          </h2>
          <p className="mt-1 text-xs text-slate-400">
            Choose a backup file to inspect it. Nothing is changed until you confirm.
          </p>
          <input
            ref={fileInput}
            type="file"
            accept="application/json,.json"
            className="hidden"
            aria-label="Choose a backup file"
            onChange={(e) => void onPickFile(e.target.files?.[0])}
          />
          <div className="mt-3">
            <Button
              variant="secondary"
              data-testid="choose-backup-file"
              onClick={() => fileInput.current?.click()}
              disabled={phase.kind === 'busy'}
              icon={<Upload className="h-4 w-4" aria-hidden />}
            >
              Choose Backup File
            </Button>
          </div>
        </section>

        <DiskBackupPanel />

        {phase.kind === 'review' ? (
          <ReviewPanel
            review={phase.review}
            acknowledged={acknowledged}
            onAcknowledged={setAcknowledged}
            onConfirm={onConfirmRestore}
            onCancel={() => {
              setAcknowledged(false);
              setPhase({ kind: 'idle' });
            }}
          />
        ) : null}

        {phase.kind === 'invalid' ? (
          <ErrorState
            title="That did not work"
            message={phase.message}
            onRetry={() => setPhase({ kind: 'idle' })}
          />
        ) : null}

        {phase.kind === 'done' ? (
          <div
            data-testid="backup-result"
            className="rounded-lg border border-emerald-800 bg-emerald-950/30 p-4 text-xs text-emerald-200"
          >
            {phase.message}
          </div>
        ) : null}
      </div>
    </section>
  );
}

/**
 * The confirmation step, and the only place a restore can be started.
 *
 * The counts are shown BEFORE the checkbox is reachable, deliberately: a user who
 * has read "this will replace 2 businesses, 41 contacts and 3 documents" is
 * making a decision. A user who has only seen "Are you sure?" is clicking.
 */
function ReviewPanel({
  review,
  acknowledged,
  onAcknowledged,
  onConfirm,
  onCancel,
}: {
  readonly review: Review;
  readonly acknowledged: boolean;
  readonly onAcknowledged: (v: boolean) => void;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}) {
  return (
    <section
      data-testid="restore-review"
      className="rounded-lg border border-amber-700 bg-amber-950/20 p-4"
    >
      <h2 className="flex items-center gap-2 text-sm font-semibold text-amber-200">
        <AlertTriangle className="h-4 w-4" aria-hidden />
        Check this file before restoring
      </h2>

      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs sm:grid-cols-3">
        <Row label="Backup taken" value={formatWhen(review.takenAt)} />
        <Row label="Data version" value={String(review.dataVersion)} />
        <Row label="Format version" value={String(review.file.formatVersion)} />
        <Row label="Contacts" value={String(review.partyCount)} />
        <Row label="Items" value={String(review.productCount)} />
        <Row label="Documents" value={String(review.documentCount)} />
      </dl>

      {/*
        The businesses are NAMED, not counted. A count cannot answer the only
        question the user actually has — "did I pick MY file?" — and two backups
        of two companies can hold identical numbers of everything.
      */}
      <div className="mt-3">
        <p className="text-slate-500">
          {review.businessNames.length === 1
            ? 'Business'
            : `Businesses (${review.businessNames.length})`}
        </p>
        <ul className="mt-0.5 list-inside list-disc text-slate-100">
          {review.businessNames.map((name) => (
            <li key={name}>{name}</li>
          ))}
        </ul>
      </div>

      <p className="mt-3 text-xs text-amber-200/90">
        Restoring replaces the data for the {review.businessCount} business(es) in this file.
        Records for other businesses are left alone. This cannot be undone — take a backup first if
        you are unsure.
      </p>

      <label className="mt-3 flex items-start gap-2 text-xs text-amber-100">
        <input
          type="checkbox"
          className="mt-0.5"
          data-testid="acknowledge-restore"
          checked={acknowledged}
          onChange={(e) => onAcknowledged(e.target.checked)}
        />
        <span>I understand this replaces existing data for these businesses.</span>
      </label>

      <div className="mt-3 flex gap-2">
        <Button
          variant="danger"
          disabled={!acknowledged}
          onClick={onConfirm}
          data-testid="confirm-restore"
        >
          Restore This Backup
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </section>
  );
}

function Row({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div>
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-medium text-slate-100">{value}</dd>
    </div>
  );
}

/** An ISO timestamp in the user's locale, or a dash if it is not readable. */
function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function errorMessage(error: unknown): string {
  if (error instanceof BackupError) return error.message;
  return error instanceof Error ? error.message : 'Something went wrong.';
}

/**
 * Automatic local-disk backup status.
 *
 * Shows enabled/disabled, the folder, the last backup, how many committed
 * changes are waiting to be checkpointed, and the last error. "Enabled" is only
 * ever reported on a platform that can reach a REAL folder (`durable`) — the
 * in-memory target used in tests and unsupported browsers never claims to be
 * backing up, because a backup that writes nowhere is not one.
 */
function DiskBackupPanel() {
  const db = getDb();
  const { user } = useAuth();
  // Memoized: a fresh target per render would re-trigger the status effect on
  // every render, polling the folder and spamming the console with rejections
  // on a device that has not chosen a folder yet.
  const { target, durable } = useMemo(() => resolveDiskBackupTarget(), []);
  const [status, setStatus] = useState<DiskBackupStatus | null>(null);
  const [busy, setBusy] = useState<'enable' | 'run' | null>(null);
  const [schedulerError, setSchedulerError] = useState<string | null>(null);
  const [schedulerLastRun, setSchedulerLastRun] = useState<string | null>(null);
  // `null` means "not probed yet". Keeping that third value is what stops the
  // panel claiming "Off" or "Paused" before it knows which one is true.
  const [readiness, setReadiness] = useState<BackupReadiness | null>(null);
  // Whether the automatic-backup timer is actually armed. Configuration alone is
  // not "On": the scheduler is the thing that makes it automatic.
  const [schedulerArmed, setSchedulerArmed] = useState(false);

  /**
   * Non-prompting folder probe — safe on a timer (it never calls
   * `requestPermission`). A probe that throws cannot prove access, so it is
   * reported as `permission_lost`: the panel then offers "Reconnect folder"
   * instead of a false "On" that would never save anything.
   */
  const probeReadiness = useCallback(() => {
    void target
      .checkReady()
      .then(setReadiness)
      .catch(() => setReadiness('permission_lost'));
  }, [target]);

  const readSchedulerFacts = useCallback(() => {
    const handle = getBackupScheduler(AUTO_BACKUP_SCHEDULER_TOKEN);
    setSchedulerArmed(handle?.armed === true);
    // Poll the scheduler's internal state so the panel can surface timer-driven
    // backup errors (permission lost, disk full, etc.) that the manifest-based
    // status does not capture.
    setSchedulerError(handle?.lastError ?? null);
    setSchedulerLastRun(handle?.lastRunAt ?? null);
  }, []);

  useEffect(() => {
    if (!durable) return;
    const refresh = () => {
      probeReadiness();
      readSchedulerFacts();
    };
    refresh();
    const id = setInterval(refresh, 2000);
    return () => clearInterval(id);
  }, [durable, probeReadiness, readSchedulerFacts]);

  // Read the manifest only once the folder is genuinely reachable. Reading it
  // earlier would make the target ask for write permission from a non-gesture
  // context — a request browsers block — and would report "access revoked" for a
  // folder that merely needs its permission re-granted by a click.
  useEffect(() => {
    if (readiness !== 'ready') return;
    let active = true;
    void getDiskBackupStatus(target)
      .then((s) => {
        if (active) setStatus(s);
      })
      .catch(() => {
        // `getDiskBackupStatus` reports failures as data and should not
        // reject; this guard exists so a regression can never turn the
        // status poll into unhandled console noise again.
      });
    return () => {
      active = false;
    };
  }, [target, readiness]);

  const apply = async (
    run: () => Promise<{ ok: boolean; status: DiskBackupStatus; error?: string }>,
    title: string,
  ) => {
    try {
      const result = await run();
      setStatus(result.status);
      if (result.ok) {
        toast({ title, description: `Folder: ${result.status.folderLabel}`, tone: 'success' });
        // Re-arm the automatic-backup scheduler after a successful manual
        // enable/reconnect. The scheduler's enable() re-checks readiness before
        // starting the timer, so this only arms it when the folder is actually
        // accessible. This still runs inside the click handler, so a permission
        // re-request would be allowed.
        if (durable) {
          await ensureAutoBackupScheduler({
            onRun: (t, now) => runDiskBackup(t.target, db, now, user?.id),
          }).catch((e) => {
            console.warn('Auto backup scheduler re-enable error:', e);
          });
        }
        // Re-read both facts immediately so the badge never lags behind the
        // action the user just took.
        probeReadiness();
        readSchedulerFacts();
      } else {
        toast({
          title: 'Automatic backup',
          description: result.error ?? 'It did not start.',
          tone: 'danger',
        });
      }
    } catch (error) {
      // The engine reports failures as data, but if anything still throws it
      // must surface as a toast — not an unhandled rejection with no feedback.
      toast({ title: 'Automatic backup', description: errorMessage(error), tone: 'danger' });
    } finally {
      setBusy(null);
    }
  };

  /** The folder is accessible and configured — only the timer is not armed. */
  const resumeScheduler = async () => {
    setBusy('enable');
    try {
      const resolved = await ensureAutoBackupScheduler({
        onRun: (t, now) => runDiskBackup(t.target, db, now, user?.id),
      });
      probeReadiness();
      readSchedulerFacts();
      toast({
        title: resolved ? 'Automatic backup resumed' : 'Automatic backup',
        description: resolved
          ? `Saving every change to ${resolved.target.folderLabel()}.`
          : 'The backup folder is not accessible yet. Reconnect it first.',
        tone: resolved ? 'success' : 'danger',
      });
    } catch (error) {
      toast({ title: 'Automatic backup', description: errorMessage(error), tone: 'danger' });
    } finally {
      setBusy(null);
    }
  };

  /**
   * The one primary action the current state calls for. `reconnect` re-requests
   * permission on the SAVED handle and never opens the picker, so a saved folder
   * can never be silently replaced by a different one.
   */
  const runAction = (kind: DiskBackupActionKind) => {
    if (kind === 'resume') {
      void resumeScheduler();
      return;
    }
    setBusy('enable');
    const now = new Date().toISOString();
    void apply(
      () =>
        kind === 'reconnect'
          ? reconnectDiskBackup(target, db, now, user?.id)
          : enableDiskBackup(target, db, now, user?.id),
      kind === 'reconnect' ? 'Automatic backup resumed' : 'Automatic backup on',
    );
  };

  // Unknown until the folder has been read: `null` keeps the panel from
  // claiming "no backup here" before it has looked.
  const configured =
    readiness === 'ready' ? (status === null ? null : status.enabled === true) : false;
  const view = deriveDiskBackupView({
    durable,
    readiness,
    configured,
    schedulerArmed,
    // The real folder name (`DirectoryHandle.name`), never a path.
    folderLabel: status?.folderLabel || target.folderLabel(),
  });
  const primaryAction = view.action;
  // Combine the manifest-based lastBackupAt with the scheduler's lastRunAt
  // for the most accurate "last successful backup" time.
  const effectiveLastBackup = (status?.lastBackupAt ?? '') || schedulerLastRun || '';
  const effectiveLastError = status?.lastError ?? schedulerError;
  const pending = status?.pendingSinceCheckpoint ?? 0;

  return (
    <section
      data-testid="disk-backup-status"
      className="mt-4 rounded-lg border border-slate-800 bg-slate-900/50 p-4"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-100">
            <ShieldCheck className="h-4 w-4" aria-hidden />
            Automatic disk backup
          </h2>
          <p
            className="mt-0.5 text-xs text-slate-400"
            data-testid="disk-backup-phase"
            data-phase={view.phase}
          >
            {view.description}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          {primaryAction ? (
            <Button
              variant="primary"
              loading={busy === 'enable'}
              loadingText="Starting…"
              onClick={() => runAction(primaryAction.kind)}
            >
              {primaryAction.label}
            </Button>
          ) : null}
          {view.canBackupNow ? (
            <Button
              variant="secondary"
              loading={busy === 'run'}
              loadingText="Backing up…"
              onClick={() => {
                setBusy('run');
                void apply(
                  () =>
                    runDiskBackup(target, db, new Date().toISOString(), user?.id),
                  'Backup updated',
                );
              }}
            >
              Back up now
            </Button>
          ) : null}
        </div>
      </div>

      {view.showDetails ? (
        <dl className="mt-3 grid grid-cols-3 gap-x-4 gap-y-1 text-xs">
          <div>
            <dt className="text-slate-500">Last backup</dt>
            <dd className="font-medium text-slate-100">
              {effectiveLastBackup ? formatWhen(effectiveLastBackup) : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-slate-500">Changes to checkpoint</dt>
            <dd className="font-medium text-slate-100" data-testid="disk-pending">
              {pending}
            </dd>
          </div>
          <div>
            <dt className="text-slate-500">Status</dt>
            <dd className="font-medium text-slate-100">
              {busy ? 'Backing up…' : view.phase === 'on' ? 'Running' : 'Idle'}
            </dd>
          </div>
        </dl>
      ) : null}

      {effectiveLastError ? (
        <p
          className="mt-3 rounded border border-red-800 bg-red-950/30 p-2 text-xs text-red-300"
          data-testid="disk-backup-error"
        >
          {effectiveLastError}
        </p>
      ) : null}
    </section>
  );
}
